import { useEffect, useMemo, useRef, useState } from "react";
import { marked } from "marked";
import DOMPurify from "dompurify";
import type { LlmEntry } from "../config";
import type { StructuredResult } from "../types";
import { copyToClipboard } from "../utils/export";
import { friendlyLabel, pillifyInPlace } from "../utils/llmLabels";
import { LlmPill } from "./LlmPill";

/**
 * Renders the rapporteur's summary as sanitized Markdown with a staggered
 * block-reveal animation — headings, paragraphs, lists, code blocks each fade
 * in one after another, left-to-right as if the report is settling onto the
 * page.
 *
 * Why this instead of a typewriter? The typewriter showed raw markdown
 * syntax character-by-character and then jumped to the rendered HTML when it
 * finished, which confused readers. A block-by-block reveal preserves the
 * cinematic feel without ever exposing the source syntax to the user.
 *
 * Security: every rendered HTML string is passed through DOMPurify before
 * being injected via dangerouslySetInnerHTML. Marked runs with GFM + line
 * breaks enabled.
 */
marked.setOptions({ gfm: true, breaks: true });

/** Delay in ms between the fade-in of two consecutive blocks. */
const BLOCK_STAGGER_MS = 90;
/** Hard ceiling on total stagger time, so a very long report doesn't keep
 *  readers waiting minutes for the last paragraph. */
const MAX_STAGGER_MS = 1800;

export function RapporteurReport({
  result,
  availableLlms,
}: {
  result: StructuredResult;
  availableLlms: LlmEntry[];
}) {
  const rap = result.rounds?.at(-1)?.rapporteur;
  const [copied, setCopied] = useState(false);
  const bodyRef = useRef<HTMLDivElement | null>(null);

  const full = rap?.summary ?? "";

  const meta = useMemo(
    () => new Map(availableLlms.map((l) => [l.id, l])),
    [availableLlms],
  );
  const knownIds = useMemo(() => availableLlms.map((l) => l.id), [availableLlms]);

  const rendered = useMemo(() => {
    if (!full) return "";
    const html = marked.parse(full, { async: false }) as string;
    return DOMPurify.sanitize(html, { USE_PROFILES: { html: true } });
  }, [full]);

  /**
   * After the report HTML is injected, assign incremental animation delays to
   * each top-level block and replace any raw LLM IDs with friendly pills.
   */
  useEffect(() => {
    const root = bodyRef.current;
    if (!root) return;
    pillifyInPlace(root, knownIds, (id) => friendlyLabel(id, meta));
    const blocks = Array.from(root.children) as HTMLElement[];
    for (const [i, el] of blocks.entries()) {
      const delay = Math.min(i * BLOCK_STAGGER_MS, MAX_STAGGER_MS);
      el.style.animationDelay = `${delay}ms`;
    }
  }, [rendered, knownIds, meta]);

  // A re-run while the current animation is still playing should replay
  // (user intuition: "my new result is settling in"), so re-key the wrapper.
  const animKey = useMemo(() => `${rendered.length}-${rendered.slice(0, 16)}`, [rendered]);

  if (!rap) return null;

  async function onCopy() {
    const ok = await copyToClipboard(full);
    if (ok) {
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    }
  }

  return (
    <section id="rapporteur-report" className="report" aria-label="Rapporteur report">
      <div className="row" style={{ justifyContent: "space-between", marginBottom: 10 }}>
        <span className="section-label">
          Rapporteur report ·{" "}
          <LlmPill id={rap.model} label={friendlyLabel(rap.model, meta)} />
        </span>
        <button
          className="btn ghost"
          onClick={onCopy}
          data-testid="copy-rapporteur"
          title="Copy the report markdown to clipboard"
        >
          {copied ? "COPIED ✓" : "COPY"}
        </button>
      </div>
      <div
        key={animKey}
        ref={bodyRef}
        className="body markdown revealing"
        data-testid="rapporteur-body"
        // `rendered` is DOMPurify-sanitized above.
        dangerouslySetInnerHTML={{ __html: rendered }}
      />
    </section>
  );
}
